import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { PayPalButtons, PayPalScriptProvider } from "@paypal/react-paypal-js";
import { BadgeCheck, CreditCard, Lock } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";
import { useAccount } from "@/context/AccountContext";
import { LAST_CHECKOUT_KEY } from "@/components/account/CreateAccountCta";
import { api, errorMessage } from "@/lib/api";

const EMAIL_RE = /^\S+@\S+\.\S+$/;
const inputCls = "h-12 w-full border border-line bg-ink px-4 text-sm text-bone outline-none transition-colors placeholder:text-smoke focus:border-acid";

const PaypalButton = ({ clientId, getPayload, prepare, valid, onPaid }) => {
  const createOrder = async () => {
    if (!(await prepare())) throw new Error("account");
    try {
      const { data } = await api.post("/checkout/paypal/create", getPayload());
      return data.paypal_order_id;
    } catch (err) {
      toast.error(errorMessage(err, "PayPal non disponibile, riprova."));
      throw err;
    }
  };
  const onApprove = async ({ orderID }) => {
    try {
      const { data } = await api.post("/checkout/paypal/capture", { paypal_order_id: orderID });
      onPaid(data);
    } catch (err) {
      toast.error(errorMessage(err, "Pagamento PayPal non completato."));
    }
  };
  return (
    <PayPalScriptProvider options={{ clientId, currency: "EUR", intent: "capture", locale: "it_IT", disableFunding: "card,credit,paylater" }}>
      <div data-testid="paypal-buttons" className={`relative z-0 transition-opacity ${valid ? "" : "pointer-events-none opacity-40"}`}>
        <PayPalButtons
          style={{ layout: "horizontal", color: "gold", shape: "rect", height: 48, tagline: false, label: "paypal" }}
          forceReRender={[valid]}
          createOrder={createOrder}
          onApprove={onApprove}
          onError={() => toast.error("Pagamento PayPal non riuscito. Riprova.")}
        />
      </div>
    </PayPalScriptProvider>
  );
};

const AccountOption = ({ customer, createAccount, setCreateAccount, password, setPassword }) => {
  if (customer) {
    return (
      <p data-testid="checkout-account-linked" className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.15em] text-acid">
        <BadgeCheck className="h-3.5 w-3.5" /> Ordine collegato al tuo account
      </p>
    );
  }
  return (
    <div className="space-y-3 border border-line p-3">
      <label className="flex cursor-pointer items-start gap-3 text-sm text-bone/80">
        <input type="checkbox" checked={createAccount} onChange={(e) => setCreateAccount(e.target.checked)} data-testid="checkout-create-account-checkbox" className="mt-0.5 h-4 w-4 accent-[#D4FF3A]" />
        <span>Crea un account per seguire la spedizione</span>
      </label>
      {createAccount && (
        <input type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Scegli una password (min. 8 caratteri)" data-testid="checkout-password-input" className={inputCls} />
      )}
      <p className="text-xs text-smoke">
        Hai già un account? <Link to="/account/accedi" data-testid="checkout-login-link" className="text-cream underline underline-offset-2">Accedi</Link> · altrimenti continui come ospite.
      </p>
    </div>
  );
};

export const CheckoutPanel = ({ onPaid }) => {
  const { items } = useCart();
  const { customer, register } = useAccount();
  const [form, setForm] = useState({ name: "", email: "" });
  const [createAccount, setCreateAccount] = useState(false);
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { data: paypal } = useQuery({ queryKey: ["paypal-config"], queryFn: () => api.get("/paypal/config").then((r) => r.data) });
  const wantsAccount = createAccount && !customer;
  const valid = form.name.trim().length >= 2 && EMAIL_RE.test(form.email) && (!wantsAccount || password.length >= 8);

  useEffect(() => {
    if (customer) setForm({ name: customer.name, email: customer.email });
  }, [customer]);

  const payloadRef = useRef(null);
  payloadRef.current = { ...form, items: items.map(({ product_id, size, qty, variant_id }) => ({ product_id, size, qty, ...(variant_id ? { variant_id } : {}) })) };
  const getPayload = () => payloadRef.current;

  const prepareRef = useRef(null);
  prepareRef.current = async () => {
    sessionStorage.setItem(LAST_CHECKOUT_KEY, JSON.stringify({ name: form.name, email: form.email }));
    if (!wantsAccount) return true;
    try {
      await register({ name: form.name, email: form.email, password });
      toast.success("Account creato", { description: "Dopo il pagamento conferma l'email per seguire la spedizione." });
      return true;
    } catch (err) {
      toast.error(errorMessage(err, "Non sono riuscito a creare l'account."));
      return false;
    }
  };
  const prepare = () => prepareRef.current();

  const payCard = async () => {
    if (!valid) {
      toast.error(wantsAccount ? "Inserisci nome, email e una password di almeno 8 caratteri" : "Inserisci nome ed email validi");
      return;
    }
    setLoading(true);
    if (!(await prepare())) {
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.post("/checkout/stripe", { ...getPayload(), origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(errorMessage(err, "Pagamento non disponibile, riprova."));
      setLoading(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="checkout-form">
      <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Nome e cognome" data-testid="checkout-name-input" className={inputCls} />
      <input type="email" value={form.email} readOnly={Boolean(customer)} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="La tua email" data-testid="checkout-email-input" className={`${inputCls} read-only:text-smoke`} />
      <AccountOption customer={customer} createAccount={createAccount} setCreateAccount={setCreateAccount} password={password} setPassword={setPassword} />
      <button
        onClick={payCard}
        disabled={loading}
        data-testid="checkout-stripe-button"
        className="flex h-12 w-full items-center justify-center gap-2 bg-cream font-mono text-[11px] uppercase tracking-[0.12em] text-ink transition-transform active:scale-[0.98] disabled:opacity-60"
      >
        <CreditCard className="h-4 w-4" /> {loading ? "Ti porto al pagamento…" : "Carta, Apple Pay o Google Pay"}
      </button>
      {paypal?.enabled && (
        <>
          <PaypalButton clientId={paypal.client_id} getPayload={getPayload} prepare={prepare} valid={valid} onPaid={onPaid} />
          {!valid && <p className="text-center font-mono text-[10px] uppercase tracking-[0.15em] text-smoke">{wantsAccount ? "Compila nome, email e password per pagare con PayPal" : "Compila nome ed email per pagare con PayPal"}</p>}
        </>
      )}
      <p className="flex items-center justify-center gap-2 pt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-smoke">
        <Lock className="h-3 w-3" /> Pagamento sicuro · Stripe{paypal?.enabled ? " & PayPal" : ""}
      </p>
      <p className="text-center text-[11px] leading-relaxed text-smoke">
        Pagando accetti le{" "}
        <a href="/condizioni#vendita" target="_blank" rel="noreferrer" data-testid="checkout-terms-link" className="text-cream underline underline-offset-2">Condizioni di vendita</a> e la{" "}
        <a href="/condizioni#privacy" target="_blank" rel="noreferrer" data-testid="checkout-privacy-link" className="text-cream underline underline-offset-2">Privacy policy</a>.
      </p>
    </div>
  );
};
